import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import DateStringInput from '@repo/ui/date-string-input'
import { ArrowUpTwoIcon, FolderIcon, TravelFolderIcon, WavingIcon } from '@repo/ui/icons'
import { useQuery } from '@tanstack/react-query'
import { parseISO } from 'date-fns'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import { getCountries } from '../../api/countries/get-countries'
import { getTripScheduleIndividuals } from '../../api/trips-schedule/trip-schedule-individuals'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, {
  DynamicDataTableTitle,
  DynamicTableRef
} from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import TransportPicto from '../../components/elements/transport-picto'
import TripsMonitoringFilter from '../../components/pages/trips-monitoring/trips-monitoring-filter'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { tripStepTypeMapping } from '../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { tripScheduleTypeEnumMapping } from '../../lib/types/enums/trips-schedule/trip-schedule-type.enum'
import {
  TripSchedule,
  TripScheduleFilters
} from '../../lib/types/models/trips-schedule/trip-schedule'
import { IdInput } from '../../lib/types/utils'
import {
  transformTripScheduleFiltersToUrl,
  transformUrlToTripScheduleFilters
} from '../../lib/utils/filters-mapping/trip-schedule'
import { constructTripFolderPath } from '../trip-folder/utils'
import TripScheduleCheckbox from './trip-schedule-checkbox'
import { markNextRow } from './utils'

const initialFilters: TripScheduleFilters = {
  date: DateFormatter.toDateFormatted(new Date())
}

const TripFolderButton = ({ tripFolderId }: { tripFolderId?: number }) => {
  const { navigate } = useNavigator()

  if (!tripFolderId) return '-'

  return (
    <Badge
      onClick={() => {
        navigate(constructTripFolderPath(tripFolderId))
      }}
      className="cursor-pointer"
      variant="primary"
      text={<FolderIcon width="20px" height="20px" color={tailwindColors.primary.DEFAULT} />}
    />
  )
}

const getArrivalCountry = (tripSchedule: IdInput<TripSchedule>) =>
  tripSchedule.arrivalPlace?.country ?? tripSchedule.country

const normalizeCountryName = (country?: string) =>
  country
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')

const getLocalTimeZone = (
  tripSchedule: IdInput<TripSchedule>,
  countries?: { name: string; timezone: string; code: string }[]
) => {
  if (tripSchedule.localTimeZone) return tripSchedule.localTimeZone

  const arrivalCountry = getArrivalCountry(tripSchedule)
  const normalizedArrivalCountry = normalizeCountryName(arrivalCountry)

  if (!normalizedArrivalCountry) return tripSchedule.timeZone

  const matchedCountry = countries?.find((country) => {
    const normalizedName = normalizeCountryName(country.name)
    const normalizedCode = normalizeCountryName(country.code)

    return (
      normalizedName === normalizedArrivalCountry || normalizedCode === normalizedArrivalCountry
    )
  })

  return matchedCountry?.timezone ?? tripSchedule.timeZone
}

const TripsMonitoringListPage = () => {
  const { t } = useTranslate()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [markedRow, setMarkedRow] = useState<string | null>(null)
  const [checkedRows, setCheckedRows] = useState<string[]>([])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripScheduleFilters>({
      initialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripScheduleFiltersToUrl,
      transformUrlToFilter: transformUrlToTripScheduleFilters
    })

  const { data } = useQuery({
    queryKey: ['trip-schedule-individuals', filter],
    queryFn: () => getTripScheduleIndividuals(filter)
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])
  const tableRef = React.useRef<DynamicTableRef<IdInput<TripSchedule>>>(null)
  const selectedDate = filter?.date ? parseISO(filter.date) : undefined

  const columns: Column<IdInput<TripSchedule>>[] = [
    {
      accessorKey: 'id',
      header: (<WavingIcon color={tailwindColors.primary.DEFAULT} />) as unknown as string,
      cell: ({ row }) => (
        <TripScheduleCheckbox
          initialChecked={checkedRows.includes(row.original.code)}
          code={row.original.code}
          onChange={(checked) => {
            if (checked) setCheckedRows((prev) => [...prev, row.original.code])
            else setCheckedRows((prev) => prev.filter((code) => code !== row.original.code))
          }}
        />
      )
    },
    {
      accessorKey: 'time',
      header: 'Heure française',
      cell: ({ row }) => {
        const originalTime = row.original.time
        const time = DateFormatter.convertTimeZone(
          { hours: originalTime.hours, minutes: originalTime.minutes },
          row.original.timeZone,
          'Europe/Paris',
          selectedDate
        )
        return (
          <div className="flex items-center gap-2">
            {row.original.code === markedRow && (
              <ArrowUpTwoIcon className="h-6 w-6" color={tailwindColors.red.DEFAULT} />
            )}
            <Badge
              text={time}
              variant={row.original.code === markedRow ? 'secondaryRed' : 'grey'}
            />
          </div>
        )
      }
    },
    {
      accessorKey: 'directionality',
      header: 'Type de trajet',
      cell: ({ row }) => (
        <Badge
          text={t(tripStepTypeMapping[row.original.directionality].label)}
          variant={tripStepTypeMapping[row.original.directionality].variant}
        />
      )
    },
    {
      accessorKey: 'type',
      header: 'Catégorie',
      cell: ({ row }) => (
        <Badge
          variant={tripScheduleTypeEnumMapping(row.original).variant}
          text={tripScheduleTypeEnumMapping(row.original).label}
        />
      )
    },
    {
      accessorKey: 'transportMeanNumber',
      header: 'N° vol/train',
      cell: ({ row }) =>
        row.original.transportMeanNumber ? <Badge text={row.original.transportMeanNumber} /> : ''
    },
    {
      accessorKey: 'transportMeans',
      header: 'Transports',
      cell: ({ row }) =>
        row.original.transportMeans?.picto ? (
          <TransportPicto picto={row.original.transportMeans?.picto} />
        ) : (
          ''
        )
    },
    {
      accessorKey: 'type',
      header: 'Détails',
      cell: ({ row }) => (
        <Badge
          text={
            <div className="flex items-center gap-2">
              {tripScheduleTypeEnumMapping(row.original).description}
              <CountryFlag
                code={row.original.countryCode}
                possibleCountry={row.original.countryCode ? undefined : row.original.country}
              />
            </div>
          }
        />
      )
    },
    {
      accessorKey: 'type',
      header: 'Heure locale',
      cell: ({ row }) => {
        const originalTime = row.original.time
        const localTimeZone = getLocalTimeZone(row.original, countries?.data)
        const time = DateFormatter.convertTimeZone(
          { hours: originalTime.hours, minutes: originalTime.minutes },
          row.original.timeZone,
          localTimeZone,
          selectedDate
        )
        return <Badge text={time} />
      }
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => <TripFolderButton tripFolderId={row.original.tripFolderId} />
    }
  ]

  return (
    <div>
      <UserNavBar title="Timeline" icon={TravelFolderIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<IdInput<TripSchedule>, TripScheduleFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        columns={columns}
        allowSearchParam
        paginated={false}
        isSearchable={false}
        displayOrder
        headerType="actions-only"
        endpoint={API.tripsSchedule.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['date'])}
        specialRows={[
          {
            borderColor: tailwindColors.red.secondary.DEFAULT,
            condition: (d) => d.code === markedRow
          },
          { color: tailwindColors.white.hover, condition: (d) => checkedRows.includes(d.code) }
        ]}
        permissions={{ delete: false }}
        postActions={
          <div className="w-48">
            <DateStringInput
              label="Date"
              value={filter?.date}
              onChange={(date) => {
                onFilterChange({ ...filter, date })
                onSelectedFilterChange({ ...filter, date })
              }}
            />
          </div>
        }
        actionLabel={
          <div className="flex items-center gap-2">
            <DynamicDataTableTitle>Timeline</DynamicDataTableTitle>

            <Badge
              text={`Total : ${data?.numberOfStudents ?? 0} jeunes + ${data?.numberOfStaffs ?? 0} encadrants`}
              variant="warning"
              size="medium"
            />
          </div>
        }
        onDataChange={(data) => {
          setCheckedRows(data.filter((d) => d.checked).map((d) => d.code))
          // Mark the row based on the selected date:
          // - Today: closest time after now
          // - Past: no arrow
          // - Future: first row
          markNextRow(data, setMarkedRow, filter?.date)
        }}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripsMonitoringFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default TripsMonitoringListPage
